import * as React from 'react';
import { DataGridPro, GridColDef, GridRenderCellParams } from '@mui/x-data-grid-pro';
import _ from 'lodash';
import { useMemo } from 'react';
import ButtonLoading from '@mui/lab/LoadingButton';
import { Checkbox, Chip, Stack, Typography } from '@mui/material';
import AddCircleOutlineRoundedIcon from '@mui/icons-material/AddCircleOutlineRounded';
import { useGetCustomRuleList, useUpdateCustomRuleStatus } from 'services/v1/Tenant/AROKMS/CustomRuleService';
import { CustomRuleListItem } from 'types/api/Tenant/AROKMS/CustomRuleTypes';
import { ButtonRecordEdit } from './ButtonRecordEdit';

// @ts-ignore
type RenderCheckBoxProps = GridRenderCellParams<any, boolean> & {
  onCheckboxChange: (checked: boolean, id: any) => void;
};

function RenderCheckBox(props: RenderCheckBoxProps) {
  const [checked, setChecked] = React.useState(props.value);

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setChecked(event.target.checked);
    props.onCheckboxChange(event.target.checked, props.id);
  };

  return (
    <Checkbox
      checked={checked}
      onChange={handleChange}
      sx={{
        color: '#828C99',
        '&.Mui-checked': { color: '#42BB93' },
      }}
    />
  );
}

export interface CustomRuleListProps {
  subjectId?: string | number;
  style?: React.CSSProperties;
  onCreateCustomRule?: () => void;
  onEditCustomRule?: (data: CustomRuleListItem) => void;
}

const dataGridStyles = {
  border: '1px solid #E3EBF6',
  borderRadius: 1.5,
  '& .MuiDataGrid-columnHeaders': {
    bgcolor: '#F8FAFC',
    borderBottom: '1px solid #E3EBF6',
    fontSize: 13,
    fontWeight: 700,
    color: '#3B4797',
  },
  '& .MuiDataGrid-cell': {
    fontSize: 13,
    borderBottom: '1px solid #F3F6F9',
  },
  '& .MuiDataGrid-row:hover': {
    bgcolor: '#FBFBFB',
  },
};

export function CustomRuleList(props: CustomRuleListProps) {
  const { subjectId, style, onCreateCustomRule = () => {}, onEditCustomRule = () => {} } = props;

  const { data: list, isLoading: isFetching } = useGetCustomRuleList(subjectId);
  const { mutate: updateCustomRuleStatus } = useUpdateCustomRuleStatus();

  const [rows, setRows] = React.useState<CustomRuleListItem[] | []>([]);

  const handleCheckboxChange = React.useCallback(
    (checked: boolean, id: any) => {
      updateCustomRuleStatus({
        customRuleId: id,
        enabled: checked,
      });
      const newRows = rows.map((row) => {
        if (row.id === id) {
          return { ...row, columnEditable: checked };
        }
        return row;
      });

      setRows(newRows);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [rows]
  );

  const columns = useMemo<GridColDef[]>(() => {
    return [
      {
        field: 'action',
        type: 'actions',
        headerName: '',
        width: 50,
        editable: false,
        filterable: false,
        renderCell: (params: GridRenderCellParams<CustomRuleListItem>) => {
          return (
            // @ts-ignore
            <ButtonRecordEdit
              onClick={() => {
                onEditCustomRule(params.row);
              }}
            />
          );
        },
      },
      {
        field: 'attributeName',
        headerName: 'Attribute',
        flex: 1,
        minWidth: 180,
        editable: false,
        filterable: false,
        renderCell: (params: GridRenderCellParams) => {
          return (
            <Stack direction='row' gap={1} alignItems='center'>
              <Typography variant='body2' fontWeight='bold' sx={{ color: '#3B4797' }}>
                {params.value}
              </Typography>
              {params.row.ruleTypeName === 'Subject Base' && (
                <Chip
                  label='Primary Key'
                  color='success'
                  variant='outlined'
                  size='small'
                  sx={{ fontSize: '10px', px: 0, height: 22 }}
                />
              )}
            </Stack>
          );
        },
      },
      {
        field: 'ruleName',
        headerName: 'Rule Name',
        flex: 1.5,
        minWidth: 250,
        editable: true,
      },
      {
        field: 'active',
        headerName: 'Enabled',
        width: 100,
        editable: false,
        filterable: false,
        renderCell: (params: GridRenderCellParams) => (
          <RenderCheckBox {...params} onCheckboxChange={handleCheckboxChange} />
        ),
      },
    ];
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [handleCheckboxChange]);

  React.useEffect(() => {
    if (list?.data) {
      setRows(_.cloneDeep(list.data));
    }
  }, [list]);

  return (
    <Stack style={style} height='100%' minHeight={450} px={3} py={2.5}>
      {/* Toolbar */}
      <Stack direction='row' justifyContent='space-between' alignItems='center' mb={2}>
        <Stack direction='row' alignItems='center' spacing={1}>
          <Typography variant='input-label' fontWeight={700}>
            Rules
          </Typography>
          {rows.length > 0 && (
            <Chip
              label={`${rows.length} ${rows.length === 1 ? 'rule' : 'rules'}`}
              size='small'
              sx={{ fontSize: '12px', height: '22px', bgcolor: '#E3EBF6', color: '#3B4797', fontWeight: 600 }}
            />
          )}
        </Stack>
        <ButtonLoading
          variant='main-table-panel'
          onClick={onCreateCustomRule}
          startIcon={<AddCircleOutlineRoundedIcon />}
          sx={{ fontSize: 13 }}
        >
          Create Rule
        </ButtonLoading>
      </Stack>

      {/* Data Grid */}
      <DataGridPro
        loading={isFetching}
        columns={columns}
        rows={rows}
        disableSelectionOnClick
        sx={dataGridStyles}
        autoHeight={rows.length > 0 && rows.length <= 8}
      />
    </Stack>
  );
}
